import { AlertCircle, Loader2, RefreshCw } from 'lucide-preact'
import { IntegrationProvider, INTEGRATION_PROVIDER_LABELS } from '@/types/integrations'
import type { IntegrationConnectionViewDto } from '@/types/integrations'
import type { FormValues } from '@/hooks/useIntegrationForm'
import { IntegrationErrorDetail, IntegrationStatusBadge } from './health'

interface IntegrationPanelBodyProps {
  formValues: FormValues
  setFormValues: (updater: FormValues | ((prev: FormValues) => FormValues)) => void
  fieldErrors: { Name?: string; Description?: string; ApiKey?: string }
  businessErrors: { Title: string; Message: string }[]
  createError: string | null
  validate: () => { Name?: string; Description?: string; ApiKey?: string }
  nameInputRef: { current: HTMLInputElement | null }
  activeProviderMeta: { id: string; title: string; desc: string; logo?: string } | null
  managingConnection: (IntegrationConnectionViewDto & { IsArchived?: boolean }) | null
  anyBusy: boolean
  creating: boolean
  updating: boolean
  archiving: boolean
  deleting: boolean
  resetting: boolean
  // Triggered by the inline "Rotate key" button on Webhook-provider manage views.
  // Opens the same confirmation modal as the footer reset path.
  onRotateKey?: () => void
  clientCompanyId?: string | null
  // Fires after a successful on-demand ErrorSummary fetch so the parent can patch
  // the cached summary back into the integrations list — prevents the skeleton
  // flashing on subsequent panel opens within the same session.
  onSummaryFetched?: (summary: string) => void
}

export function IntegrationPanelBody({
  formValues, setFormValues, fieldErrors, businessErrors, createError,
  validate, nameInputRef, activeProviderMeta, managingConnection,
  anyBusy, creating, updating, resetting,
  onRotateKey, clientCompanyId, onSummaryFetched,
}: IntegrationPanelBodyProps) {
  const hasErrors = managingConnection?.RecentErrors && managingConnection.RecentErrors.length > 0
  // Webhook-provider API keys are generated server-side — don't show the input on create.
  const hideApiKeyInput = activeProviderMeta?.id === IntegrationProvider.Webhook
  const isWebhookManage = managingConnection?.Provider === IntegrationProvider.Webhook
  return (
    <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
      {(businessErrors.length > 0 || createError) && (
        <div className="p-3 rounded-md border text-sm bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 space-y-2">
          {businessErrors.length > 0 ? (
            <ul className="space-y-2">
              {businessErrors.map((e, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium leading-snug">{e.Title}</p>
                    <p className="text-[11px] leading-snug opacity-90">{e.Message}</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{createError}</span>
            </div>
          )}
        </div>
      )}

      <div className="space-y-6">
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Connection Name<span className="text-red-500 ml-0.5">*</span>
            </label>
            <input
              ref={nameInputRef}
              value={formValues.Name}
              onInput={e => {
                setFormValues(f => ({ ...f, Name: (e.currentTarget as HTMLInputElement).value }))
                if (createError) validate()
              }}
              onBlur={() => validate()}
              maxLength={110}
              className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${fieldErrors.Name ? 'border-red-400 dark:border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600'}`}
              placeholder="Connection name"
              disabled={anyBusy}
              autoFocus
              aria-invalid={!!fieldErrors.Name}
              aria-describedby={fieldErrors.Name ? 'integration-name-error' : undefined}
            />
            {fieldErrors.Name && <p id="integration-name-error" className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.Name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</label>
            <textarea
              value={formValues.Description}
              onInput={e => {
                setFormValues(f => ({ ...f, Description: (e.currentTarget as HTMLTextAreaElement).value }))
                if (createError) validate()
              }}
              onBlur={() => validate()}
              rows={3}
              maxLength={520}
              className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none ${fieldErrors.Description ? 'border-red-400 dark:border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600'}`}
              placeholder="Optional description"
              disabled={anyBusy}
              aria-invalid={!!fieldErrors.Description}
              aria-describedby={fieldErrors.Description ? 'integration-description-error' : undefined}
            />
            <div className="mt-1 flex items-center justify-between">
              {fieldErrors.Description && <p id="integration-description-error" className="text-xs text-red-600 dark:text-red-400">{fieldErrors.Description}</p>}
              <p className="text-[10px] text-slate-400 dark:text-slate-500 ml-auto">{formValues.Description.length}/500</p>
            </div>
          </div>

          {activeProviderMeta && !hideApiKeyInput && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                API Key<span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                type="password"
                value={formValues.ApiKey}
                onInput={e => {
                  setFormValues(f => ({ ...f, ApiKey: (e.currentTarget as HTMLInputElement).value }))
                  if (createError) validate()
                }}
                onBlur={() => validate()}
                className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${fieldErrors.ApiKey ? 'border-red-400 dark:border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600'}`}
                placeholder="Paste API key"
                disabled={creating}
                aria-invalid={!!fieldErrors.ApiKey}
                aria-describedby={fieldErrors.ApiKey ? 'integration-apikey-error' : undefined}
              />
              {fieldErrors.ApiKey && <p id="integration-apikey-error" className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.ApiKey}</p>}
            </div>
          )}

          {activeProviderMeta && hideApiKeyInput && (
            <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-3 text-xs text-slate-600 dark:text-slate-300">
              An API key will be generated for you when this connection is created. It will be shown once — copy and store it in a safe place immediately.
            </div>
          )}


          {managingConnection && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Current API Key</label>
                <div className="flex gap-2 items-stretch">
                  <input
                    type="password"
                    value="••••••••••••••••••••"
                    readOnly
                    disabled
                    className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 focus:outline-none"
                  />
                  {isWebhookManage && onRotateKey && (
                    <button
                      type="button"
                      onClick={onRotateKey}
                      disabled={anyBusy}
                      className="px-3 py-2 rounded-lg border border-amber-300 dark:border-amber-600 bg-white dark:bg-slate-800 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 flex items-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Generate a new key and invalidate the current one"
                    >
                      {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Rotate key
                    </button>
                  )}
                </div>
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                  {isWebhookManage
                    ? 'Keys are shown once when created or rotated. Rotating invalidates the current key immediately — update your caller before rotating.'
                    : 'Keys are stored encrypted and never displayed after creation. Enter a new key below to rotate.'}
                </p>
              </div>

              {!isWebhookManage && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">New API Key <span className="font-normal text-slate-400 dark:text-slate-500">(optional)</span></label>
                  <input type="password" value={formValues.ApiKey} onInput={e => setFormValues(f => ({ ...f, ApiKey: (e.currentTarget as HTMLInputElement).value }))} className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="Enter new key to rotate" disabled={updating} />
                  <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">Leave blank to keep the existing key. Enter a new key to rotate.</p>
                </div>
              )}

            </div>
          )}
        </div>

        {managingConnection && (
          <div className="space-y-2 text-xs text-slate-500 dark:text-slate-400">
            <h5 className="text-slate-700 dark:text-slate-200 font-semibold text-sm">Metadata</h5>
            <ul className="space-y-1">
              <li><span className="font-medium">Provider:</span> {INTEGRATION_PROVIDER_LABELS[managingConnection.Provider || ''] || managingConnection.Provider}</li>
              <li><span className="font-medium">Auth Type:</span> API Key</li>
              <li><span className="font-medium">Created:</span> {managingConnection.CreatedUtc ? new Date(managingConnection.CreatedUtc).toLocaleString() : '—'}</li>
              <li><span className="font-medium">Updated:</span> {managingConnection.UpdatedUtc ? new Date(managingConnection.UpdatedUtc).toLocaleString() : '—'}</li>
              <li>
                <span className="font-medium">Status:</span>{' '}
                <IntegrationStatusBadge status={managingConnection.Status} isSoftFailure={managingConnection.IsSoftFailure} />
              </li>
            </ul>
          </div>
        )}

        {managingConnection && hasErrors && (
          <IntegrationErrorDetail
            status={managingConnection.Status}
            isSoftFailure={managingConnection.IsSoftFailure}
            consecutiveFailureCount={managingConnection.ConsecutiveFailureCount}
            recentErrors={managingConnection.RecentErrors}
            aiSummary={managingConnection.AiSummary}
            clientCompanyId={clientCompanyId}
            connectionId={managingConnection.Id}
            onSummaryFetched={onSummaryFetched}
          />
        )}

        {managingConnection && managingConnection.Provider === IntegrationProvider.Webhook && (
          <WebhookUsageSnippet />
        )}

        {!managingConnection && activeProviderMeta && (
          <div className="space-y-2 text-xs text-slate-500 dark:text-slate-400">
            <h5 className="text-slate-700 dark:text-slate-200 font-semibold text-sm">About {activeProviderMeta.title}</h5>
            <p className="leading-relaxed">{activeProviderMeta.desc}</p>
            {activeProviderMeta.id === IntegrationProvider.Webhook && (
              <div className="space-y-2 leading-relaxed">
                <p>
                  Creating this connection generates a per-company API key that external callers use to post form submissions into OrbitalX.
                  Incoming submissions flow through the same ingest pipeline as landing-page leads, so validation, rate limiting, and downstream
                  enrichment all apply automatically.
                </p>
                <p>
                  Callers authenticate by sending the key in an <code className="px-1 rounded bg-slate-100 dark:bg-slate-700">Authorization: Bearer &lt;key&gt;</code> header.
                  Once the connection is saved you&apos;ll see the generated key exactly once &mdash; copy it immediately and store it somewhere safe (password manager, secret vault, CI config). The key is never shown again; if you lose it, use the Reset action to rotate to a new one.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function WebhookUsageSnippet() {
  const apiBase = ((import.meta as ImportMeta).env?.VITE_API_BASE_URL || '').replace(/\/$/, '')
  // FormSubmissions/Webhook, not .../Submit (ORB-496). Submit is now the landing-page route and is
  // gated by the public API edge's managed identity, so a Bearer key posted there gets a 401.
  const endpoint = `${apiBase}/api/FormSubmissions/Webhook`
  // WorkspaceId is required by the API and has no default. The integration is scoped to a client
  // company, which can own several workspaces, so the caller has to choose one — hence a placeholder
  // rather than a real id. Threading workspace selection into this panel is ORB-497.
  const curlExample = `curl -X POST '${endpoint}?WorkspaceId=<YOUR_WORKSPACE_ID>' \\
  -H 'Authorization: Bearer <YOUR_KEY>' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "Email": "lead@example.com",
    "FirstName": "Jane",
    "LastName": "Doe",
    "Fields": { "Company": "Acme Inc." }
  }'`
  const copy = (text: string) => { navigator.clipboard?.writeText(text) }
  return (
    <div className="space-y-3 text-xs text-slate-600 dark:text-slate-300">
      <h5 className="text-slate-700 dark:text-slate-200 font-semibold text-sm">Usage</h5>
      <p className="leading-relaxed">
        Use this integration to post form submissions into OrbitalX from any external system &mdash; Zapier, legacy CRMs, custom scripts,
        one-off backfills, or server-to-server pipelines. Submissions land in the same ingest pipeline as landing-page leads, so
        validation, rate limiting, and downstream enrichment all apply automatically.
      </p>
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="font-medium">Endpoint</span>
          <button type="button" onClick={() => copy(endpoint)} className="text-[10px] text-primary-600 dark:text-primary-400 hover:underline">Copy</button>
        </div>
        <pre className="p-2 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-x-auto whitespace-pre">POST {endpoint}</pre>
      </div>
      <div className="space-y-1">
        <p className="font-medium text-slate-700 dark:text-slate-200">Authentication</p>
        <p className="leading-relaxed">
          Every request must include an <code className="px-1 rounded bg-slate-100 dark:bg-slate-700">Authorization</code> header using the
          <strong> Bearer</strong> scheme, with the API key from this connection as the token:
        </p>
        <pre className="p-2 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-x-auto whitespace-pre">Authorization: Bearer &lt;YOUR_KEY&gt;</pre>
        <p className="leading-relaxed">
          The key is scoped to this client company and is shown exactly once &mdash; when the connection is first created, and whenever you
          rotate it with the <strong>Reset</strong> action above. There is no way to view the current key after that, by design: store it in
          a password manager or secret vault as soon as it&apos;s revealed. Rotating invalidates the old key immediately, so update your
          caller <em>before</em> resetting. <strong>Do not</strong> place the key in a query string, a request body field, or a log line;
          it must only ever appear in the <code className="px-1 rounded bg-slate-100 dark:bg-slate-700">Authorization</code> header.
          Requests without a valid bearer token receive a <code className="px-1 rounded bg-slate-100 dark:bg-slate-700">401 Unauthorized</code>.
        </p>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="font-medium">curl example</span>
          <button type="button" onClick={() => copy(curlExample)} className="text-[10px] text-primary-600 dark:text-primary-400 hover:underline">Copy</button>
        </div>
        <pre className="p-2 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-x-auto whitespace-pre">{curlExample}</pre>
      </div>
    </div>
  )
}
